import { useCallback, useContext, useEffect, useState } from "react";
import { Timestamp } from "@bufbuild/protobuf";
import { OrgApiKeyService } from "@buf/brevdev_devplane.connectrpc_es/devplaneapi/v1/org_api_key_connect";
import {
  OrgApiKeyType,
} from "@buf/brevdev_devplane.bufbuild_es/devplaneapi/v1/org_api_key_pb";
import type {
  OrgAPIKey as OrgAPIKeyPB,
  KeyRole as KeyRolePB,
} from "@buf/brevdev_devplane.bufbuild_es/devplaneapi/v1/org_api_key_pb";
import { createConnectClient } from "server/connect";
import { OrgContext } from "contexts/OrgContext";

export type OrgAPIKey = {
  id: string;
  name: string;
  keyPrefix: string;
  roleIds: string[];
  expiresAt: string;
  userId: string;
  orgId: string;
  keyType: OrgApiKeyType;
  createTime?: string;
  updateTime?: string;
  lastUsedAt?: string;
};

export type KeyRole = {
  id: string;
  displayName: string;
  description: string;
  category: string;
};

type CreateOrgAPIKeyArgs = {
  name: string;
  roleIds: string[];
  expiresAt: string;
  orgId: string;
};

type UpdateOrgAPIKeyArgs = {
  id: string;
  name: string;
  roleIds: string[];
  expiresAt: string;
};

const client = createConnectClient(OrgApiKeyService);
type ListOrgAPIKeyRequest = Parameters<typeof client.listOrgAPIKey>[0];

const makeListOrgAPIKeyRequest = (orgId: string): ListOrgAPIKeyRequest =>
  ({
    orgId,
    options: {
      orgId,
    },
  }) as unknown as ListOrgAPIKeyRequest;

function messageFromError(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

function tsToISO(ts?: Timestamp): string | undefined {
  if (!ts) return undefined;
  return ts.toDate().toISOString();
}

function isoToTimestamp(iso: string): Timestamp {
  const trimmed = iso.trim();
  if (!trimmed) {
    throw new Error("API key expiration is required");
  }
  const date = new Date(trimmed);
  if (isNaN(date.getTime())) {
    throw new Error("API key expiration must be a valid date");
  }
  return Timestamp.fromDate(date);
}

function fromPB(pb: OrgAPIKeyPB): OrgAPIKey {
  return {
    id: pb.id,
    name: pb.name,
    keyPrefix: pb.keyPrefix,
    roleIds: pb.roleIds,
    expiresAt: tsToISO(pb.expiresAt) ?? "",
    userId: pb.userId,
    orgId: pb.orgId,
    keyType: pb.keyType,
    createTime: tsToISO(pb.createTime),
    updateTime: tsToISO(pb.updateTime),
    lastUsedAt: tsToISO(pb.lastUsedAt),
  };
}

function roleFromPB(pb: KeyRolePB): KeyRole {
  return {
    id: pb.id,
    displayName: pb.displayName,
    description: pb.description,
    category: pb.category,
  };
}

export const useOrgAPIKeys = () => {
  const { activeOrgId } = useContext(OrgContext);
  const [keys, setKeys] = useState<OrgAPIKey[]>([]);
  const [availableRoles, setAvailableRoles] = useState<KeyRole[]>([]);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isMutating, setIsMutating] = useState(false);
  const [error, setError] = useState("");

  const fetchKeys = useCallback(
    async (showLoading = false) => {
      if (showLoading) setIsInitialLoading(true);
      setError("");
      if (!activeOrgId) {
        setKeys([]);
        if (showLoading) setIsInitialLoading(false);
        return;
      }
      try {
        const res = await client.listOrgAPIKey(
          makeListOrgAPIKeyRequest(activeOrgId),
        );
        setKeys(res.items.map(fromPB));
      } catch (e) {
        console.error("Failed to fetch API keys", e);
        setError(messageFromError(e, "Failed to fetch API keys"));
      } finally {
        if (showLoading) setIsInitialLoading(false);
      }
    },
    [activeOrgId],
  );

  const fetchRoles = useCallback(async () => {
    try {
      const res = await client.listKeyRoles({});
      setAvailableRoles(res.roles.map(roleFromPB));
    } catch (e) {
      console.error("Failed to fetch key roles", e);
      setError(messageFromError(e, "Failed to fetch key roles"));
    }
  }, []);

  useEffect(() => {
    fetchKeys(true);
    fetchRoles();
  }, [fetchKeys, fetchRoles]);

  const createKey = useCallback(
    async (
      args: CreateOrgAPIKeyArgs,
    ): Promise<{ apiKey: OrgAPIKey | null; plaintextValue: string }> => {
      setIsMutating(true);
      setError("");
      try {
        const res = await client.createOrgAPIKey({
          name: args.name,
          roleIds: args.roleIds,
          expiresAt: isoToTimestamp(args.expiresAt),
          orgId: args.orgId,
        });
        await fetchKeys();
        return {
          apiKey: res.apiKey ? fromPB(res.apiKey) : null,
          plaintextValue: res.plaintextValue,
        };
      } catch (e) {
        setError(messageFromError(e, "Failed to create API key"));
        throw e;
      } finally {
        setIsMutating(false);
      }
    },
    [fetchKeys],
  );

  const updateKey = useCallback(
    async (args: UpdateOrgAPIKeyArgs) => {
      setIsMutating(true);
      setError("");
      try {
        await client.updateOrgAPIKey({
          id: args.id,
          name: args.name,
          roleIds: args.roleIds,
          expiresAt: isoToTimestamp(args.expiresAt),
        });
        await fetchKeys();
      } catch (e) {
        setError(messageFromError(e, "Failed to update API key"));
        throw e;
      } finally {
        setIsMutating(false);
      }
    },
    [fetchKeys],
  );

  const deleteKey = useCallback(
    async (id: string) => {
      setIsMutating(true);
      setError("");
      try {
        await client.deleteOrgAPIKey({ id });
        await fetchKeys();
      } catch (e) {
        setError(messageFromError(e, "Failed to delete API key"));
        throw e;
      } finally {
        setIsMutating(false);
      }
    },
    [fetchKeys],
  );

  return {
    keys,
    availableRoles,
    isLoading: isInitialLoading,
    isInitialLoading,
    isMutating,
    error,
    createKey,
    updateKey,
    deleteKey,
    refetch: () => fetchKeys(true),
  };
};
